import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Row, Button, Space } from 'antd';
import _invoke from 'lodash/invoke';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { usePrevious, useSocket } from 'hooks';
import {
  getConversationListRequest,
  getUnreadConversationsRequest,
  getSeenButNotRepliedRequest,
  getConversationStatsRequest,
  setConversationFilter,
  conversationSelectors,
  messageSelectors,
  getMessageListRequest,
  sendMessageRequest,
  getConversationDetailRequest,
  seenConversationRequest,
  firstGetListMessageFalse,
  triggerModalTag,
  getViegoTagsRequest,
  resetMessengerState,
  sendingMessage,
  finishSendMessage,
  handleIncomingMessage,
  markMessageAsRecalled,
  updateMessageText,
  updateUserStatus,
} from 'providers/MessengerProvider/slice';
import { getBillingListRequest } from 'providers/BillingProvider/slice';
import './styles.less';
import Helper from 'utils/helpers';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageInput from 'containers/Dashboard/Fonehouse/Messenger/MessageList/MessageInput';
import MessageList from 'containers/Dashboard/Fonehouse/Messenger/MessageList';
import ConversationHeader from 'containers/Dashboard/Fonehouse/Messenger/MessageList/ConversationHeader';
import ConversationList from 'containers/Dashboard/Fonehouse/Messenger/ConversationList';
import CreateTagModal from 'containers/Dashboard/Fonehouse/Messenger/CreateTagModal';
import SetTagModal from 'containers/Dashboard/Fonehouse/Messenger/SetTagModal';
import FilterTag from 'containers/Dashboard/Fonehouse/Messenger/FilterTag';

const CONVERSATION_TYPE = 'VIETGO_TO_USER';
const CONVERSATION_ROLE = 'fonehouse';
const BASE_ROUTE = '/cms_viego_support/viego_messengers';

const ViegoSupportMessenger = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const formRef = useRef();
  const conversationEntitiesRef = useRef();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserId = _get(currentUser, '_id');
  const currentUserRole = _get(currentUser, 'role');
  const { conversationId } = useParams();
  const conversationIds = useSelector(conversationSelectors.selectIds);
  const conversationEntities = useSelector(conversationSelectors.selectEntities);
  conversationEntitiesRef.current = conversationEntities;
  const conversationHasMore = useSelector((state) => state.messenger.conversations.hasMore);
  const conversationPage = useSelector((state) => state.messenger.conversations.page);
  const conversationFilter = useSelector((state) => state.messenger.conversationFilter);
  const isGettingList = useSelector((state) => state.messenger.isGettingList);
  const conversationStats = useSelector((state) => state.messenger.conversationStats);
  const opponentUserId = _get(conversationEntities[conversationId], 'member[0]');
  const billingList = useSelector((state) => state.billingList.docs);
  const latestBill = billingList[0];

  const messageIds = useSelector(messageSelectors.selectIds);
  const messageEntities = useSelector(messageSelectors.selectEntities);
  const messageHasMore = useSelector((state) => state.messenger.messages.hasMore);
  const lastMessageTime = useSelector((state) => state.messenger.messages.lastMessageTime);
  const firstGetList = useSelector((state) => state.messenger.firstGetList);
  const isGettingMessageList = useSelector((state) => state.messenger.isGettingMessageList);
  const { isRemovingTag, isSending } = useSelector((state) => state.messenger);
  const lastSentMessage = useSelector((state) => state.messenger.lastSentMessage);
  const firstConversationId = conversationIds[0];
  const prevFirstConversationId = usePrevious(firstConversationId);
  const { socket, isConnected } = useSocket();
  const isFirstMount = useRef(true);

  // Reset shared state + fetch fresh data on mount
  // All 3 messenger pages share the same Redux slice, so reset prevents stale data from other pages
  useEffect(() => {
    dispatch(resetMessengerState());
    dispatch(getViegoTagsRequest());
    dispatch(getConversationStatsRequest({ type: CONVERSATION_TYPE }));
  }, [dispatch]);

  // Define functions first before using them in useEffect
  const handleGetMessageList = useCallback(() => {
    dispatch(
      getMessageListRequest({
        limit: 15,
        time: lastMessageTime,
        conversationId,
      })
    );
  }, [conversationId, dispatch, lastMessageTime]);

  const handleGetConversationList = useCallback(() => {
    const params = {
      limit: 30,
      page: conversationPage + 1,
      type: CONVERSATION_TYPE,
      role: CONVERSATION_ROLE,
    };

    if (conversationFilter === 'unread') {
      dispatch(getUnreadConversationsRequest({ ...params, unreadOnly: true }));
    } else if (conversationFilter === 'notReplied') {
      dispatch(getSeenButNotRepliedRequest(params));
    } else {
      dispatch(getConversationListRequest(params));
    }
  }, [conversationPage, dispatch, conversationFilter]);

  const handleConversationCellClick = useCallback(
    (_conversationId) => {
      if (_conversationId === conversationId) {
        return;
      }

      dispatch(firstGetListMessageFalse());
      navigate(`${BASE_ROUTE}/${_conversationId}`, { replace: true });
    },
    [dispatch, navigate, conversationId]
  );

  const handleFilterChange = useCallback(
    (key) => {
      dispatch(setConversationFilter(key));
      const params = { limit: 30, page: 1, type: CONVERSATION_TYPE, role: CONVERSATION_ROLE };

      if (key === 'unread') {
        dispatch(getUnreadConversationsRequest(params));
      } else if (key === 'notReplied') {
        dispatch(getSeenButNotRepliedRequest(params));
      } else {
        dispatch(getConversationListRequest(params));
      }
    },
    [dispatch]
  );

  const refreshConversationList = useCallback(() => {
    // Refresh stats for badge counts
    dispatch(getConversationStatsRequest({ type: CONVERSATION_TYPE }));

    if (conversationFilter === 'unread') {
      dispatch(
        getUnreadConversationsRequest({
          limit: 30,
          page: 1,
          type: CONVERSATION_TYPE,
          role: CONVERSATION_ROLE,
          unreadOnly: true,
        })
      );
    } else if (conversationFilter === 'notReplied') {
      dispatch(
        getSeenButNotRepliedRequest({
          limit: 30,
          page: 1,
          type: CONVERSATION_TYPE,
          role: CONVERSATION_ROLE,
        })
      );
    } else {
      dispatch(
        getConversationListRequest({
          limit: 30,
          page: 1,
          type: CONVERSATION_TYPE,
          role: CONVERSATION_ROLE,
        })
      );
    }
  }, [dispatch, conversationFilter]);

  // Debounced refresh to prevent excessive API calls on rapid messages
  const debouncedRefresh = useMemo(
    () => _debounce(() => refreshConversationList(), 500, { leading: false, trailing: true }),
    [refreshConversationList]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedRefresh.cancel();
    };
  }, [debouncedRefresh]);

  // Socket: new_message handler
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      const setupConnection = () => {
        // Join admin room để nhận conversation-change (badge, preview)
        socket.emit('unread-conversations', { room: 'admin-room' });
        // Subscribe conversation cụ thể để nhận new_message
        socket.emit('subscribe', { room: conversationId });
      };

      // Initial setup
      setupConnection();

      // Re-setup on reconnect
      socket.on('connect', setupConnection);

      const handleNewMessage = (data) => {
        const dataConversationId = _get(data, 'message.conversationId');
        const serverMessage = _get(data, 'message');

        // new_message chỉ đến từ conversation room đang subscribe
        if (dataConversationId === conversationId) {
          dispatch(handleIncomingMessage({ serverMessage }));
          dispatch(finishSendMessage());
        }
        debouncedRefresh();
      };

      socket.on('new_message', handleNewMessage);
      return () => {
        socket.off('connect', setupConnection);
        socket.off('new_message', handleNewMessage);
        // Unsubscribe khỏi conversation room khi rời
        socket.emit('unsubscribe', { room: conversationId });
      };
    }
  }, [conversationId, currentUserId, dispatch, debouncedRefresh, socket, isConnected]);

  // Socket: conversation-change handler
  // Chỉ xử lý conversation thuộc type VIETGO_TO_USER (đã có trong danh sách)
  // Tránh lẫn conversation từ Fonehouse/Consulate vào store chung
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      const handleConversationChange = (data) => {
        const changedConvId = data?.conversationId;
        // Chỉ fetch detail nếu conversation đã có trong danh sách hiện tại
        if (changedConvId && conversationEntitiesRef.current[changedConvId]) {
          dispatch(getConversationDetailRequest(data));
        }
        // Luôn refresh stats + list để cập nhật badge và phát hiện conversation mới
        debouncedRefresh();
      };
      socket.on('conversation-change', handleConversationChange);
      return () => {
        socket.off('conversation-change', handleConversationChange);
      };
    }
  }, [currentUserId, dispatch, debouncedRefresh, socket, isConnected]);

  // Socket: message-recalled handler
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      const handleMessageRecalled = (data) => {
        const { messageId } = data;
        dispatch(markMessageAsRecalled({ messageId }));
      };
      socket.on('message-recalled', handleMessageRecalled);
      return () => {
        socket.off('message-recalled', handleMessageRecalled);
      };
    }
  }, [currentUserId, dispatch, socket, isConnected]);

  // Socket: message-updated handler
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      const handleMessageUpdated = (data) => {
        const { messageId, text, isEdited, editedAt } = data;
        dispatch(updateMessageText({ messageId, text, isEdited, editedAt }));
      };
      socket.on('message-updated', handleMessageUpdated);
      return () => {
        socket.off('message-updated', handleMessageUpdated);
      };
    }
  }, [currentUserId, dispatch, socket, isConnected]);

  // Socket: user-status-change handler (online/offline)
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      const handleUserStatusChange = (data) => {
        const { userId, online, lastSeen } = data;
        dispatch(updateUserStatus({ userId, online, lastSeen }));
      };
      socket.on('user-status-change', handleUserStatusChange);
      return () => {
        socket.off('user-status-change', handleUserStatusChange);
      };
    }
  }, [currentUserId, dispatch, socket, isConnected]);

  // Auto-redirect to first conversation when none selected
  // Skip on first mount to avoid redirecting with stale data from previous page
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (!prevFirstConversationId && firstConversationId && !conversationId) {
      navigate(`${BASE_ROUTE}/${firstConversationId}`, {
        replace: true,
      });
    }
  }, [conversationId, firstConversationId, navigate, prevFirstConversationId]);

  useEffect(() => {
    if (conversationFilter === 'unread') {
      dispatch(
        getUnreadConversationsRequest({
          limit: 30,
          page: 1,
          type: CONVERSATION_TYPE,
          role: CONVERSATION_ROLE,
        })
      );
    } else if (conversationFilter === 'notReplied') {
      dispatch(
        getSeenButNotRepliedRequest({
          limit: 30,
          page: 1,
          type: CONVERSATION_TYPE,
          role: CONVERSATION_ROLE,
        })
      );
    } else {
      dispatch(
        getConversationListRequest({
          limit: 30,
          page: 1,
          type: CONVERSATION_TYPE,
          role: CONVERSATION_ROLE,
        })
      );
    }
  }, [dispatch, conversationFilter]);

  useEffect(() => {
    if (conversationId) {
      dispatch(seenConversationRequest({ conversationId }));
      dispatch(getMessageListRequest({ limit: 50, conversationId }));
    }
  }, [conversationId, dispatch]);

  useEffect(() => {
    if (opponentUserId) {
      dispatch(getBillingListRequest({ userId: opponentUserId, limit: 1, page: 1 }));
    }
  }, [dispatch, opponentUserId]);

  // Socket emit when message sent successfully
  useEffect(() => {
    if (lastSentMessage && socket && isConnected) {
      socket.emit('new_message_admin', {
        room: lastSentMessage.conversationId,
        messageId: lastSentMessage.messageId,
      });
    }
  }, [lastSentMessage, socket, isConnected]);

  const handleSendMessage = useCallback(
    async (values) => {
      const trimmedMessage = _invoke(values.message, 'trim');
      const hasAttachments = !_isEmpty(values.attachments);
      const hasTemplateImages = !_isEmpty(values.templateImages);

      // Guard: prevent empty message submit
      if (!trimmedMessage && !hasAttachments && !hasTemplateImages) {
        return;
      }

      // Set sending state (disables button)
      dispatch(sendingMessage());

      // Parse product tag from message
      const { hasProductTag, productId, cleanText } = Helper.parseProductTag(trimmedMessage);
      const messageType = hasProductTag ? 'product' : 'text';
      const messageText = hasProductTag ? cleanText : trimmedMessage;

      try {
        // Reset form immediately for better UX
        formRef.current.resetForm();

        let attachments = [];

        // Handle regular file attachments (need uploading)
        if (!_isEmpty(values.attachments)) {
          const imageFiles = values.attachments.filter((file) => file.type === 'image');
          const videoFiles = values.attachments.filter((file) => file.type === 'video');
          const totalFiles = imageFiles.length + videoFiles.length;

          if (totalFiles > 0) {
            setUploading(true);
            setUploadProgress(0);

            const fileProgresses = {};

            // Track progress for each file
            const handleProgress = (fileIndex) => (progress) => {
              fileProgresses[fileIndex] = progress;
              const totalProgress = Object.values(fileProgresses).reduce((sum, p) => sum + p, 0);
              const avgProgress = Math.round(totalProgress / totalFiles);
              setUploadProgress(avgProgress);
            };

            // Upload videos (no progress tracking available, mark 100% on complete)
            const videoPromises = videoFiles.map((file, idx) =>
              Helper.uploadVideo(file).then((res) => {
                handleProgress(idx)(100);
                return res;
              })
            );

            // Upload images with progress
            const imagePromises = imageFiles.map((file, idx) =>
              Helper.uploadFile(file, handleProgress(videoFiles.length + idx))
            );

            const results = await Promise.all([...videoPromises, ...imagePromises]);
            const uploadedAttachments = results.filter(Boolean).map((res) => ({
              type: res.attachmentType,
              data: res.attachmentType === 'image' ? { imageUrl: res.url } : { videoUrl: res.url },
            }));
            attachments = attachments.concat(uploadedAttachments);
            setUploading(false);
            setUploadProgress(0);
          }
        }

        // Handle template images (already uploaded, just need URLs)
        if (!_isEmpty(values.templateImages)) {
          const templateAttachments = values.templateImages.map((img) => ({
            type: 'image',
            data: { imageUrl: img.url },
          }));
          attachments = attachments.concat(templateAttachments);
        }

        // Dispatch the send message request
        if (messageText || !_isEmpty(attachments) || hasProductTag) {
          dispatch(sendMessageRequest({
            type: messageType,
            text: messageText || null,
            conversationId,
            attachments,
            ...(hasProductTag && { productId }),
          }));
        }
      } catch (error) {
        setUploading(false);
        setUploadProgress(0);
        dispatch(finishSendMessage());
      }
    },
    [conversationId, dispatch]
  );

  const handleShowModalTag = useCallback(() => {
    dispatch(
      triggerModalTag({
        visible: true,
        conversationId,
        conversationEntities,
        conversationType: CONVERSATION_TYPE,
      })
    );
  }, [dispatch, conversationEntities, conversationId]);

  return (
    <div className="viego-support-messenger-page">
      {isRemovingTag && <LoadingIndicator />}
      <Row
        className="page-header"
        align="middle"
        style={{
          padding: '12px 16px',
          backgroundColor: '#f5f5f5',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Nhắn tin VieGo Support</h2>
      </Row>
      <Row
        className="color-legend"
        align="middle"
        style={{
          marginBottom: 0,
          padding: '12px 16px',
          backgroundColor: '#fff',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <FilterTag conversationFilter={conversationFilter} conversationType={CONVERSATION_TYPE} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              fontSize: 12,
              color: '#666',
            }}
          >
            <span style={{ fontWeight: 500 }}>Chú thích màu sắc:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: '#bfffe2',
                  borderRadius: 4,
                  border: '1px solid #ddd',
                }}
              ></div>
              <span>Tin nhắn mới</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: '#ff4d4f',
                  borderRadius: 4,
                }}
              ></div>
              <span>Chưa trả lời</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: 'white',
                  borderRadius: 4,
                  border: '1px solid #ddd',
                }}
              ></div>
              <span>Đã đọc và đã trả lời</span>
            </div>
          </div>
        </div>
        <Space>
          <Button size="large" type="primary" onClick={handleShowModalTag}>
            Tạo nhãn dán
          </Button>
        </Space>
      </Row>
      <div className="message-page-content" style={{ marginTop: 0 }}>
        <div className="conversation-list-section">
          {/* Tabs section - horizontal layout */}
          <div
            style={{
              padding: '6px 8px',
              borderBottom: '1px solid #f0f0f0',
              background: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '4px',
            }}
          >
            <button
              onClick={() => handleFilterChange('all')}
              style={{
                padding: '4px 8px',
                border: 'none',
                background: conversationFilter === 'all' ? '#e6f7ff' : 'transparent',
                color: conversationFilter === 'all' ? '#1890ff' : '#333',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: conversationFilter === 'all' ? '500' : '400',
                transition: 'all 0.2s',
                outline: 'none',
                whiteSpace: 'nowrap',
                lineHeight: '1.5',
                borderRadius: '4px',
              }}
            >
              Tất cả
              {conversationStats.totalConversations > 0 && (
                <span style={{ marginLeft: '4px', fontSize: '11px', color: '#999' }}>
                  ({conversationStats.totalConversations})
                </span>
              )}
            </button>
            <button
              onClick={() => handleFilterChange('unread')}
              style={{
                padding: '4px 8px',
                border: 'none',
                background: conversationFilter === 'unread' ? '#e6f7ff' : 'transparent',
                color: conversationFilter === 'unread' ? '#1890ff' : '#333',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: conversationFilter === 'unread' ? '500' : '400',
                transition: 'all 0.2s',
                outline: 'none',
                whiteSpace: 'nowrap',
                lineHeight: '1.5',
                borderRadius: '4px',
              }}
            >
              Chưa đọc
              {conversationStats.unreadCount > 0 && (
                <span
                  style={{
                    marginLeft: '4px',
                    background: '#ff4d4f',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '1px 5px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  {conversationStats.unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => handleFilterChange('notReplied')}
              style={{
                padding: '4px 8px',
                border: 'none',
                background: conversationFilter === 'notReplied' ? '#e6f7ff' : 'transparent',
                color: conversationFilter === 'notReplied' ? '#1890ff' : '#333',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: conversationFilter === 'notReplied' ? '500' : '400',
                transition: 'all 0.2s',
                outline: 'none',
                whiteSpace: 'nowrap',
                lineHeight: '1.5',
                borderRadius: '4px',
              }}
            >
              Chưa trả lời
              {conversationStats.seenButNotRepliedCount > 0 && (
                <span
                  style={{
                    marginLeft: '4px',
                    background: '#faad14',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '1px 5px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  {conversationStats.seenButNotRepliedCount}
                </span>
              )}
            </button>
          </div>
          <ConversationList
            activeConversationId={conversationId}
            onConversationCellClick={handleConversationCellClick}
            conversationIds={conversationIds}
            hasMore={conversationHasMore}
            getConversationList={handleGetConversationList}
            conversationEntities={conversationEntities}
            currentUserId={currentUserId}
            isGettingList={isGettingList}
            conversationType={CONVERSATION_TYPE}
          />
        </div>
        <div className="conversation-container">
          <ConversationHeader conversationDetail={conversationEntities[conversationId]} latestBill={latestBill} />
          <MessageList
            firstGetList={firstGetList}
            isGettingMessageList={isGettingMessageList}
            hasMore={messageHasMore}
            getMessageList={handleGetMessageList}
            messageIds={messageIds}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            messageEntities={messageEntities}
          />
          <MessageInput
            conversationId={conversationId}
            ref={formRef}
            uploading={uploading}
            uploadProgress={uploadProgress}
            onClick={handleSendMessage}
            isSending={isSending}
          />
        </div>
      </div>
      <CreateTagModal />
      <SetTagModal />
    </div>
  );
};

export default ViegoSupportMessenger;
